import { jsPDF } from 'jspdf';
import { formatFilterSummary, FilterSummary } from './exportView';
import type { Initiative, Product, Quarter, Classification, Status } from '@/types';

// ── Canvas export: data-driven PNG/PDF rendering ────────────────────────────────
// Renders timeline/list views directly to a canvas from initiative data,
// avoiding the fragile DOM-cloning approach.

// ── Scale ───────────────────────────────────────────────────────────────────────
const SCALE = 2; // Retina quality

// ── Shared layout (1x CSS-like pixels) ──────────────────────────────────────────
const HEADER_H = 64;
const LEGEND_H = 32;
const LEGEND_PAD = 8;

// ── Colors ──────────────────────────────────────────────────────────────────────
const BG_WHITE = '#FFFFFF';
const HEADER_BG = '#F1F5F9';
const PRODUCT_BG = '#EFF6FF';
const SUBPROD_BG = '#F8FAFC';
const TEXT_PRIMARY = '#111827';
const TEXT_SECONDARY = '#6B7280';
const TEXT_TERTIARY = '#9CA3AF';
const BORDER_LIGHT = '#F3F4F6';
const BORDER_DEFAULT = '#E5E7EB';

// ── Timeline constants ──────────────────────────────────────────────────────────
const TL_NAME_W = 240;
const TL_QUARTER_W = 200;
const TL_COL_HEADER_H = 30;
const TL_PRODUCT_ROW_H = 30;
const TL_SUBPROD_ROW_H = 26;
const TL_BAR_H = 58;
const TL_BAR_GAP = 6;
const TL_ROW_PAD = 8;
const TL_TITLE_LINE_H = 13;
const TL_TITLE_MAX_LINES = 3;
const TL_TITLE_FONT = '500 10px Inter, -apple-system, system-ui, sans-serif';
const TL_STATUS_FONT = '9px Inter, -apple-system, system-ui, sans-serif';

// ── List constants ──────────────────────────────────────────────────────────────
const LI_COL_NAME = 360;
const LI_COL_CLASS = 130;
const LI_COL_STATUS = 120;
const LI_COL_START = 90;
const LI_COL_END = 90;
const LI_TABLE_W = LI_COL_NAME + LI_COL_CLASS + LI_COL_STATUS + LI_COL_START + LI_COL_END;
const LI_HEADER_H = 28;
const LI_ROW_H = 26;
const LI_INIT_ROW_H = 48;
const LI_TITLE_LINE_H = 12;
const LI_TITLE_MAX_LINES = 3;
const LI_PAD_LEFT = 8;

// ── PDF pagination constants ─────────────────────────────────────────────────
const PDF_PAGE_H = 780;
const PDF_QS_PER_PAGE = 4;
const PDF_LEGEND_Y = PDF_PAGE_H - LEGEND_H;
const PDF_CONTENT_END_Y = PDF_LEGEND_Y - LEGEND_PAD;

interface PdfPageData {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  width: number;
}

// ── Types ───────────────────────────────────────────────────────────────────────
interface ProductGroup {
  product: Product;
  subProducts: { name: string; initiatives: Initiative[] }[];
}

export interface CanvasExportOptions {
  viewType: 'timeline' | 'list';
  format: 'png' | 'pdf';
  filterSummary: FilterSummary;
  initiatives: Initiative[];
  products: Product[];
  quarters: Quarter[];
  classifications: Classification[];
  statuses: Status[];
}

// ── Helpers ─────────────────────────────────────────────────────────────────────

function getTimestamp(): string {
  const now = new Date();
  const date = now.toISOString().split('T')[0];
  const time = now.toTimeString().split(' ')[0].replace(/:/g, '');
  return `${date}_${time}`;
}

/** Blend hex color with white at given opacity */
function blendWithWhite(hexColor: string, opacity: number): string {
  const h = hexColor.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const br = Math.round(r * opacity + 255 * (1 - opacity));
  const bg = Math.round(g * opacity + 255 * (1 - opacity));
  const bb = Math.round(b * opacity + 255 * (1 - opacity));
  return `rgb(${br},${bg},${bb})`;
}

/** Draw a rounded rectangle */
function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

/** Truncate text to fit within maxWidth, adding ellipsis */
function truncateText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let truncated = text;
  while (truncated.length > 0 && ctx.measureText(truncated + '...').width > maxWidth) {
    truncated = truncated.slice(0, -1);
  }
  return truncated + '...';
}

/** Wrap text into lines that fit within maxWidth, up to maxLines.
 *  Last line is truncated with ellipsis if text overflows. */
function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number,
): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (ctx.measureText(testLine).width <= maxWidth) {
      currentLine = testLine;
    } else {
      if (currentLine) {
        lines.push(currentLine);
        if (lines.length >= maxLines) {
          // Truncate last line with ellipsis
          lines[lines.length - 1] = truncateText(ctx, lines[lines.length - 1], maxWidth);
          return lines;
        }
      }
      // Word itself may be wider than maxWidth — force it on its own line
      currentLine = word;
    }
  }
  if (currentLine) {
    lines.push(currentLine);
  }

  // If we exceeded maxLines, truncate the last one
  if (lines.length > maxLines) {
    const kept = lines.slice(0, maxLines);
    kept[maxLines - 1] = truncateText(ctx, kept[maxLines - 1], maxWidth);
    return kept;
  }

  return lines;
}

/** Group initiatives by product > sub-product */
function groupByProduct(initiatives: Initiative[], products: Product[]): ProductGroup[] {
  const bySubProduct = new Map<string, Initiative[]>();
  initiatives.forEach(init => {
    if (!bySubProduct.has(init.subProductId)) bySubProduct.set(init.subProductId, []);
    bySubProduct.get(init.subProductId)!.push(init);
  });

  const groups: ProductGroup[] = [];
  for (const product of products) {
    const subs: ProductGroup['subProducts'] = [];
    for (const sp of product.subProducts || []) {
      const inits = bySubProduct.get(sp.id);
      if (inits && inits.length > 0) {
        // Sort by start quarter
        const sorted = [...inits].sort((a, b) => {
          const aIdx = initiatives.indexOf(a);
          const bIdx = initiatives.indexOf(b);
          return aIdx - bIdx;
        });
        subs.push({ name: sp.name, initiatives: sorted });
      }
    }
    if (subs.length > 0) groups.push({ product, subProducts: subs });
  }
  return groups;
}

// ── Drawing functions ───────────────────────────────────────────────────────────

/** Draw export header (title, filters, timestamp) */
function drawHeader(
  ctx: CanvasRenderingContext2D,
  options: CanvasExportOptions,
  totalWidth: number,
) {
  // Background
  ctx.fillStyle = HEADER_BG;
  ctx.fillRect(0, 0, totalWidth, HEADER_H);

  // Title
  ctx.fillStyle = TEXT_PRIMARY;
  ctx.font = 'bold 15px Inter, -apple-system, system-ui, sans-serif';
  const viewName = options.viewType === 'timeline' ? 'Timeline' : 'List';
  ctx.fillText(`CrossProd \u2014 ${viewName} View`, 16, 22);

  // Filters
  ctx.fillStyle = TEXT_SECONDARY;
  ctx.font = '11px Inter, -apple-system, system-ui, sans-serif';
  const filterStr = `Filters: ${formatFilterSummary(options.filterSummary)}`;
  const truncatedFilter = truncateText(ctx, filterStr, totalWidth - 32);
  ctx.fillText(truncatedFilter, 16, 40);

  // Timestamp
  ctx.fillStyle = TEXT_TERTIARY;
  ctx.font = '10px Inter, -apple-system, system-ui, sans-serif';
  const exportDate = new Date().toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
  ctx.fillText(`Exported: ${exportDate}  \u2022  ${options.initiatives.length} Initiatives`, 16, 56);

  // Bottom border
  ctx.strokeStyle = BORDER_DEFAULT;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, HEADER_H);
  ctx.lineTo(totalWidth, HEADER_H);
  ctx.stroke();
}

/** Draw classification + status legend */
function drawLegend(
  ctx: CanvasRenderingContext2D,
  classifications: Classification[],
  statuses: Status[],
  y: number,
  totalWidth: number,
) {
  // Background
  ctx.fillStyle = HEADER_BG;
  ctx.fillRect(0, y, totalWidth, LEGEND_H);

  // Top border
  ctx.strokeStyle = BORDER_DEFAULT;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, y);
  ctx.lineTo(totalWidth, y);
  ctx.stroke();

  let x = 12;
  const textY = y + LEGEND_H / 2 + 4;

  // Classification legend
  ctx.fillStyle = TEXT_SECONDARY;
  ctx.font = 'bold 9px Inter, -apple-system, system-ui, sans-serif';
  ctx.fillText('CLASSIFICATION:', x, textY);
  x += ctx.measureText('CLASSIFICATION:').width + 8;

  ctx.font = '9px Inter, -apple-system, system-ui, sans-serif';
  classifications.filter(c => c.isActive).sort((a, b) => a.displayOrder - b.displayOrder).forEach(cls => {
    // Color square
    ctx.fillStyle = cls.color;
    ctx.fillRect(x, y + LEGEND_H / 2 - 4, 8, 8);
    x += 11;
    // Name
    ctx.fillStyle = TEXT_PRIMARY;
    ctx.fillText(cls.name, x, textY);
    x += ctx.measureText(cls.name).width + 12;
  });

  // Status legend
  x += 8;
  ctx.fillStyle = TEXT_SECONDARY;
  ctx.font = 'bold 9px Inter, -apple-system, system-ui, sans-serif';
  ctx.fillText('STATUS:', x, textY);
  x += ctx.measureText('STATUS:').width + 8;

  ctx.font = '9px Inter, -apple-system, system-ui, sans-serif';
  statuses.filter(s => s.isActive).sort((a, b) => a.displayOrder - b.displayOrder).forEach(st => {
    // Color dot
    ctx.fillStyle = st.color;
    ctx.beginPath();
    ctx.arc(x + 4, y + LEGEND_H / 2, 4, 0, Math.PI * 2);
    ctx.fill();
    x += 12;
    // Name
    ctx.fillStyle = TEXT_PRIMARY;
    ctx.fillText(st.name, x, textY);
    x += ctx.measureText(st.name).width + 12;
  });
}

// ── Timeline rendering ──────────────────────────────────────────────────────────

function calcTimelineDimensions(
  groups: ProductGroup[],
  numQuarters: number,
): { width: number; height: number } {
  const width = TL_NAME_W + numQuarters * TL_QUARTER_W;
  let height = HEADER_H + TL_COL_HEADER_H;

  for (const group of groups) {
    height += TL_PRODUCT_ROW_H;
    for (const sp of group.subProducts) {
      height += TL_SUBPROD_ROW_H;
      height += TL_ROW_PAD + sp.initiatives.length * (TL_BAR_H + TL_BAR_GAP) + TL_ROW_PAD;
    }
  }

  height += LEGEND_PAD + LEGEND_H;
  return { width, height };
}

function drawTimeline(
  ctx: CanvasRenderingContext2D,
  options: CanvasExportOptions,
  groups: ProductGroup[],
) {
  const { quarters } = options;
  const numQ = quarters.length;
  const totalW = TL_NAME_W + numQ * TL_QUARTER_W;
  let curY = HEADER_H;

  // Column header row
  ctx.fillStyle = HEADER_BG;
  ctx.fillRect(0, curY, totalW, TL_COL_HEADER_H);

  ctx.fillStyle = TEXT_PRIMARY;
  ctx.font = 'bold 11px Inter, -apple-system, system-ui, sans-serif';
  ctx.fillText('Product / Initiative', 10, curY + TL_COL_HEADER_H / 2 + 4);

  quarters.forEach((q, qi) => {
    const x = TL_NAME_W + qi * TL_QUARTER_W;
    ctx.fillStyle = HEADER_BG;
    ctx.fillRect(x, curY, TL_QUARTER_W, TL_COL_HEADER_H);
    ctx.fillStyle = TEXT_PRIMARY;
    ctx.font = 'bold 11px Inter, -apple-system, system-ui, sans-serif';
    const tw = ctx.measureText(q.fullLabel).width;
    ctx.fillText(q.fullLabel, x + (TL_QUARTER_W - tw) / 2, curY + TL_COL_HEADER_H / 2 + 4);
  });

  // Column header bottom border
  ctx.strokeStyle = BORDER_DEFAULT;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, curY + TL_COL_HEADER_H);
  ctx.lineTo(totalW, curY + TL_COL_HEADER_H);
  ctx.stroke();

  curY += TL_COL_HEADER_H;

  // Quarter vertical dividers (full height below header)
  const contentStartY = curY;
  const dims = calcTimelineDimensions(groups, numQ);
  const contentEndY = dims.height - LEGEND_PAD - LEGEND_H;

  for (let qi = 1; qi < numQ; qi++) {
    const x = TL_NAME_W + qi * TL_QUARTER_W;
    ctx.strokeStyle = BORDER_LIGHT;
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(x, contentStartY);
    ctx.lineTo(x, contentEndY);
    ctx.stroke();
  }

  // Name column right border
  ctx.strokeStyle = BORDER_DEFAULT;
  ctx.lineWidth = 0.5;
  ctx.beginPath();
  ctx.moveTo(TL_NAME_W, contentStartY);
  ctx.lineTo(TL_NAME_W, contentEndY);
  ctx.stroke();

  // Render groups
  for (const group of groups) {
    // Product header row
    const prodCount = group.subProducts.reduce((s, sp) => s + sp.initiatives.length, 0);
    ctx.fillStyle = PRODUCT_BG;
    ctx.fillRect(0, curY, totalW, TL_PRODUCT_ROW_H);
    ctx.strokeStyle = BORDER_LIGHT;
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(0, curY + TL_PRODUCT_ROW_H);
    ctx.lineTo(totalW, curY + TL_PRODUCT_ROW_H);
    ctx.stroke();

    ctx.fillStyle = TEXT_PRIMARY;
    ctx.font = 'bold 11px Inter, -apple-system, system-ui, sans-serif';
    ctx.fillText(`${group.product.name} (${prodCount})`, 10, curY + TL_PRODUCT_ROW_H / 2 + 4);
    curY += TL_PRODUCT_ROW_H;

    for (const sp of group.subProducts) {
      // Sub-product header row
      ctx.fillStyle = SUBPROD_BG;
      ctx.fillRect(0, curY, totalW, TL_SUBPROD_ROW_H);
      ctx.strokeStyle = BORDER_LIGHT;
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(0, curY + TL_SUBPROD_ROW_H);
      ctx.lineTo(totalW, curY + TL_SUBPROD_ROW_H);
      ctx.stroke();

      ctx.fillStyle = TEXT_SECONDARY;
      ctx.font = '500 10px Inter, -apple-system, system-ui, sans-serif';
      ctx.fillText(sp.name, 18, curY + TL_SUBPROD_ROW_H / 2 + 3);
      curY += TL_SUBPROD_ROW_H;

      // Initiative bars
      const barAreaY = curY + TL_ROW_PAD;
      sp.initiatives.forEach((init, idx) => {
        const startIdx = quarters.findIndex(q => q.key === init.startQuarter);
        const endIdx = init.endQuarter
          ? quarters.findIndex(q => q.key === init.endQuarter)
          : startIdx;
        if (startIdx < 0) return;

        const barX = TL_NAME_W + startIdx * TL_QUARTER_W + 4;
        const barY = barAreaY + idx * (TL_BAR_H + TL_BAR_GAP);
        const barSpan = Math.max(1, (endIdx >= 0 ? endIdx : startIdx) - startIdx + 1);
        const barW = barSpan * TL_QUARTER_W - 8;

        const clsColor = init.classification?.color || '#3B82F6';
        const statusColor = init.status?.color || '#6B7280';
        const hasDeps = init.dependencies && init.dependencies.length > 0;

        // Bar background with rounded corners
        ctx.fillStyle = blendWithWhite(clsColor, 0.12);
        roundRect(ctx, barX, barY, barW, TL_BAR_H, 4);
        ctx.fill();

        // Bar border
        ctx.strokeStyle = clsColor + '60';
        ctx.lineWidth = 1;
        roundRect(ctx, barX, barY, barW, TL_BAR_H, 4);
        ctx.stroke();

        // Classification left accent
        ctx.fillStyle = clsColor;
        ctx.fillRect(barX, barY + 2, 3, TL_BAR_H - 4);

        // ── Title area (top portion, wraps up to 3 lines) ──
        const contentX = barX + 10;
        const titleMaxW = barW - 14;
        const titleTopY = barY + 6;

        ctx.fillStyle = TEXT_PRIMARY;
        ctx.font = TL_TITLE_FONT;
        const titleLines = wrapText(ctx, init.title, titleMaxW, TL_TITLE_MAX_LINES);
        titleLines.forEach((line, li) => {
          ctx.fillText(line, contentX, titleTopY + li * TL_TITLE_LINE_H + 9);
        });

        // ── Status row (bottom of bar) ──
        const statusRowY = barY + TL_BAR_H - 14;

        // Status dot
        ctx.fillStyle = statusColor;
        ctx.beginPath();
        ctx.arc(contentX + 4, statusRowY + 4, 3.5, 0, Math.PI * 2);
        ctx.fill();

        // Status name
        ctx.fillStyle = TEXT_SECONDARY;
        ctx.font = TL_STATUS_FONT;
        ctx.fillText(init.status?.name || '', contentX + 12, statusRowY + 7);

        // Dependency chain icon (bottom-right)
        if (hasDeps) {
          ctx.fillStyle = TEXT_SECONDARY;
          ctx.font = '10px Inter, -apple-system, system-ui, sans-serif';
          ctx.fillText('\u{1F517}', barX + barW - 16, statusRowY + 7);
        }
      });

      curY += TL_ROW_PAD + sp.initiatives.length * (TL_BAR_H + TL_BAR_GAP) + TL_ROW_PAD;
    }
  }
}

// ── List rendering ──────────────────────────────────────────────────────────────

function calcListDimensions(groups: ProductGroup[]): { width: number; height: number } {
  let height = HEADER_H + LI_HEADER_H;

  for (const group of groups) {
    height += LI_ROW_H; // product row
    for (const sp of group.subProducts) {
      height += LI_ROW_H; // sub-product row
      height += sp.initiatives.length * LI_INIT_ROW_H; // initiative rows (taller for wrapped titles)
    }
  }

  height += LEGEND_PAD + LEGEND_H;
  return { width: LI_TABLE_W, height };
}

function drawList(
  ctx: CanvasRenderingContext2D,
  options: CanvasExportOptions,
  groups: ProductGroup[],
) {
  const { quarters } = options;
  let curY = HEADER_H;

  // Column positions
  const cols = [
    { label: 'Initiative Name', x: 0, w: LI_COL_NAME },
    { label: 'Classification', x: LI_COL_NAME, w: LI_COL_CLASS },
    { label: 'Status', x: LI_COL_NAME + LI_COL_CLASS, w: LI_COL_STATUS },
    { label: 'Start', x: LI_COL_NAME + LI_COL_CLASS + LI_COL_STATUS, w: LI_COL_START },
    { label: 'End', x: LI_COL_NAME + LI_COL_CLASS + LI_COL_STATUS + LI_COL_START, w: LI_COL_END },
  ];

  // Table header
  ctx.fillStyle = HEADER_BG;
  ctx.fillRect(0, curY, LI_TABLE_W, LI_HEADER_H);

  ctx.fillStyle = TEXT_PRIMARY;
  ctx.font = 'bold 10px Inter, -apple-system, system-ui, sans-serif';
  cols.forEach(col => {
    ctx.fillText(col.label, col.x + LI_PAD_LEFT, curY + LI_HEADER_H / 2 + 3.5);
  });

  ctx.strokeStyle = BORDER_DEFAULT;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, curY + LI_HEADER_H);
  ctx.lineTo(LI_TABLE_W, curY + LI_HEADER_H);
  ctx.stroke();

  curY += LI_HEADER_H;

  // Render rows
  for (const group of groups) {
    const prodCount = group.subProducts.reduce((s, sp) => s + sp.initiatives.length, 0);

    // Product row
    ctx.fillStyle = PRODUCT_BG;
    ctx.fillRect(0, curY, LI_TABLE_W, LI_ROW_H);
    ctx.fillStyle = TEXT_PRIMARY;
    ctx.font = 'bold 10px Inter, -apple-system, system-ui, sans-serif';
    ctx.fillText(`${group.product.name} (${prodCount})`, LI_PAD_LEFT, curY + LI_ROW_H / 2 + 3.5);

    ctx.strokeStyle = BORDER_LIGHT;
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(0, curY + LI_ROW_H);
    ctx.lineTo(LI_TABLE_W, curY + LI_ROW_H);
    ctx.stroke();

    curY += LI_ROW_H;

    for (const sp of group.subProducts) {
      // Sub-product row
      ctx.fillStyle = SUBPROD_BG;
      ctx.fillRect(0, curY, LI_TABLE_W, LI_ROW_H);
      ctx.fillStyle = TEXT_SECONDARY;
      ctx.font = '500 10px Inter, -apple-system, system-ui, sans-serif';
      ctx.fillText(sp.name, 16, curY + LI_ROW_H / 2 + 3.5);

      ctx.strokeStyle = BORDER_LIGHT;
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(0, curY + LI_ROW_H);
      ctx.lineTo(LI_TABLE_W, curY + LI_ROW_H);
      ctx.stroke();

      curY += LI_ROW_H;

      // Initiative rows
      for (const init of sp.initiatives) {
        const clsColor = init.classification?.color || '#3B82F6';
        const statusColor = init.status?.color || '#6B7280';
        const hasDeps = init.dependencies && init.dependencies.length > 0;
        const startQ = quarters.find(q => q.key === init.startQuarter);
        const endQ = init.endQuarter ? quarters.find(q => q.key === init.endQuarter) : null;
        const midY = curY + LI_INIT_ROW_H / 2;

        // Row background (white)
        ctx.fillStyle = BG_WHITE;
        ctx.fillRect(0, curY, LI_TABLE_W, LI_INIT_ROW_H);

        // Classification color pipe
        ctx.fillStyle = clsColor;
        ctx.fillRect(cols[0].x + 22, curY + 4, 3, LI_INIT_ROW_H - 8);

        // Initiative name (wrapped up to 3 lines) + dependency icon
        ctx.fillStyle = TEXT_PRIMARY;
        ctx.font = '500 10px Inter, -apple-system, system-ui, sans-serif';
        const depSuffix = hasDeps ? '  \u{1F517}' : '';
        const titleLines = wrapText(ctx, init.title + depSuffix, LI_COL_NAME - 36, LI_TITLE_MAX_LINES);
        titleLines.forEach((line, li) => {
          ctx.fillText(line, cols[0].x + 30, curY + 14 + li * LI_TITLE_LINE_H);
        });

        // Classification: colored square + name
        ctx.fillStyle = clsColor;
        ctx.fillRect(cols[1].x + LI_PAD_LEFT, midY - 4, 7, 7);
        ctx.fillStyle = TEXT_PRIMARY;
        ctx.font = '9px Inter, -apple-system, system-ui, sans-serif';
        const clsName = truncateText(ctx, init.classification?.name || '', LI_COL_CLASS - 26);
        ctx.fillText(clsName, cols[1].x + LI_PAD_LEFT + 11, midY + 3);

        // Status: dot + name
        ctx.fillStyle = statusColor;
        ctx.beginPath();
        ctx.arc(cols[2].x + LI_PAD_LEFT + 4, midY, 3.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = TEXT_PRIMARY;
        ctx.font = '9px Inter, -apple-system, system-ui, sans-serif';
        ctx.fillText(init.status?.name || '', cols[2].x + LI_PAD_LEFT + 13, midY + 3);

        // Start quarter
        ctx.fillStyle = TEXT_PRIMARY;
        ctx.font = '9px Inter, -apple-system, system-ui, sans-serif';
        ctx.fillText(startQ?.fullLabel || '', cols[3].x + LI_PAD_LEFT, midY + 3);

        // End quarter
        ctx.fillText(endQ?.fullLabel || '\u2013', cols[4].x + LI_PAD_LEFT, midY + 3);

        // Row bottom border
        ctx.strokeStyle = BORDER_LIGHT;
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        ctx.moveTo(0, curY + LI_INIT_ROW_H);
        ctx.lineTo(LI_TABLE_W, curY + LI_INIT_ROW_H);
        ctx.stroke();

        curY += LI_INIT_ROW_H;
      }
    }
  }
}

// ── PDF paginated export ─────────────────────────────────────────────────────

function createPdfPage(width: number): PdfPageData {
  const canvas = document.createElement('canvas');
  canvas.width = width * SCALE;
  canvas.height = PDF_PAGE_H * SCALE;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(SCALE, SCALE);
  ctx.fillStyle = BG_WHITE;
  ctx.fillRect(0, 0, width, PDF_PAGE_H);
  return { canvas, ctx, width };
}

function exportPaginatedPdf(
  options: CanvasExportOptions,
  groups: ProductGroup[],
): void {
  const { viewType, quarters, classifications, statuses } = options;
  const pages: PdfPageData[] = [];

  if (viewType === 'timeline') {
    // ── Timeline: chunk quarters horizontally, render-as-you-go vertically ──
    const qChunks: Quarter[][] = [];
    for (let i = 0; i < quarters.length; i += PDF_QS_PER_PAGE) {
      qChunks.push(quarters.slice(i, i + PDF_QS_PER_PAGE));
    }

    for (let ci = 0; ci < qChunks.length; ci++) {
      const chunk = qChunks[ci];
      const chunkStart = ci * PDF_QS_PER_PAGE;
      const chunkEnd = chunkStart + chunk.length - 1;
      const pageW = TL_NAME_W + chunk.length * TL_QUARTER_W;

      let pg = createPdfPage(pageW);
      pages.push(pg);
      drawHeader(pg.ctx, options, pageW);
      let curY = HEADER_H;

      // ── Column header helper ──
      const drawColHeaders = (ctx: CanvasRenderingContext2D, y: number) => {
        ctx.fillStyle = HEADER_BG;
        ctx.fillRect(0, y, pageW, TL_COL_HEADER_H);
        ctx.fillStyle = TEXT_PRIMARY;
        ctx.font = 'bold 11px Inter, -apple-system, system-ui, sans-serif';
        ctx.fillText('Product / Initiative', 10, y + TL_COL_HEADER_H / 2 + 4);
        chunk.forEach((q, qi) => {
          const x = TL_NAME_W + qi * TL_QUARTER_W;
          ctx.fillStyle = HEADER_BG;
          ctx.fillRect(x, y, TL_QUARTER_W, TL_COL_HEADER_H);
          ctx.fillStyle = TEXT_PRIMARY;
          ctx.font = 'bold 11px Inter, -apple-system, system-ui, sans-serif';
          const tw = ctx.measureText(q.fullLabel).width;
          ctx.fillText(q.fullLabel, x + (TL_QUARTER_W - tw) / 2, y + TL_COL_HEADER_H / 2 + 4);
        });
        ctx.strokeStyle = BORDER_DEFAULT;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, y + TL_COL_HEADER_H);
        ctx.lineTo(pageW, y + TL_COL_HEADER_H);
        ctx.stroke();
      };

      drawColHeaders(pg.ctx, curY);
      curY += TL_COL_HEADER_H;

      // ── Quarter dividers helper ──
      const drawDividers = (ctx: CanvasRenderingContext2D, fromY: number) => {
        for (let qi = 1; qi < chunk.length; qi++) {
          const x = TL_NAME_W + qi * TL_QUARTER_W;
          ctx.strokeStyle = BORDER_LIGHT;
          ctx.lineWidth = 0.5;
          ctx.beginPath();
          ctx.moveTo(x, fromY);
          ctx.lineTo(x, PDF_CONTENT_END_Y);
          ctx.stroke();
        }
        ctx.strokeStyle = BORDER_DEFAULT;
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        ctx.moveTo(TL_NAME_W, fromY);
        ctx.lineTo(TL_NAME_W, PDF_CONTENT_END_Y);
        ctx.stroke();
      };

      drawDividers(pg.ctx, curY);

      // ── New page helper ──
      const newPage = () => {
        drawLegend(pg.ctx, classifications, statuses, PDF_LEGEND_Y, pageW);
        pg = createPdfPage(pageW);
        pages.push(pg);
        drawHeader(pg.ctx, options, pageW);
        curY = HEADER_H;
        drawColHeaders(pg.ctx, curY);
        curY += TL_COL_HEADER_H;
        drawDividers(pg.ctx, curY);
      };

      // ── Product row helper ──
      const drawProdRow = (ctx: CanvasRenderingContext2D, group: ProductGroup, y: number) => {
        const count = group.subProducts.reduce((s, sp) => s + sp.initiatives.length, 0);
        ctx.fillStyle = PRODUCT_BG;
        ctx.fillRect(0, y, pageW, TL_PRODUCT_ROW_H);
        ctx.strokeStyle = BORDER_LIGHT;
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        ctx.moveTo(0, y + TL_PRODUCT_ROW_H);
        ctx.lineTo(pageW, y + TL_PRODUCT_ROW_H);
        ctx.stroke();
        ctx.fillStyle = TEXT_PRIMARY;
        ctx.font = 'bold 11px Inter, -apple-system, system-ui, sans-serif';
        ctx.fillText(`${group.product.name} (${count})`, 10, y + TL_PRODUCT_ROW_H / 2 + 4);
      };

      // ── Sub-product row helper ──
      const drawSpRow = (ctx: CanvasRenderingContext2D, name: string, y: number) => {
        ctx.fillStyle = SUBPROD_BG;
        ctx.fillRect(0, y, pageW, TL_SUBPROD_ROW_H);
        ctx.strokeStyle = BORDER_LIGHT;
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        ctx.moveTo(0, y + TL_SUBPROD_ROW_H);
        ctx.lineTo(pageW, y + TL_SUBPROD_ROW_H);
        ctx.stroke();
        ctx.fillStyle = TEXT_SECONDARY;
        ctx.font = '500 10px Inter, -apple-system, system-ui, sans-serif';
        ctx.fillText(name, 18, y + TL_SUBPROD_ROW_H / 2 + 3);
      };

      // ── Initiative bar helper (clips to visible quarter chunk) ──
      const drawBar = (ctx: CanvasRenderingContext2D, init: Initiative, barY: number) => {
        const si = quarters.findIndex(q => q.key === init.startQuarter);
        const rawEi = init.endQuarter ? quarters.findIndex(q => q.key === init.endQuarter) : si;
        const ei = rawEi >= 0 ? rawEi : si;
        if (si < 0) return;

        const visStart = Math.max(si, chunkStart) - chunkStart;
        const visEnd = Math.min(ei, chunkEnd) - chunkStart;
        const barX = TL_NAME_W + visStart * TL_QUARTER_W + 4;
        const barSpan = visEnd - visStart + 1;
        const barW = barSpan * TL_QUARTER_W - 8;

        const clsColor = init.classification?.color || '#3B82F6';
        const statusColor = init.status?.color || '#6B7280';
        const hasDeps = init.dependencies && init.dependencies.length > 0;

        // Background
        ctx.fillStyle = blendWithWhite(clsColor, 0.12);
        roundRect(ctx, barX, barY, barW, TL_BAR_H, 4);
        ctx.fill();

        // Border
        ctx.strokeStyle = clsColor + '60';
        ctx.lineWidth = 1;
        roundRect(ctx, barX, barY, barW, TL_BAR_H, 4);
        ctx.stroke();

        // Left accent
        ctx.fillStyle = clsColor;
        ctx.fillRect(barX, barY + 2, 3, TL_BAR_H - 4);

        // Title (wraps up to 3 lines)
        const contentX = barX + 10;
        const titleMaxW = barW - 14;
        ctx.fillStyle = TEXT_PRIMARY;
        ctx.font = TL_TITLE_FONT;
        const titleLines = wrapText(ctx, init.title, titleMaxW, TL_TITLE_MAX_LINES);
        titleLines.forEach((line, li) => {
          ctx.fillText(line, contentX, barY + 6 + li * TL_TITLE_LINE_H + 9);
        });

        // Status row (bottom)
        const statusRowY = barY + TL_BAR_H - 14;
        ctx.fillStyle = statusColor;
        ctx.beginPath();
        ctx.arc(contentX + 4, statusRowY + 4, 3.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = TEXT_SECONDARY;
        ctx.font = TL_STATUS_FONT;
        ctx.fillText(init.status?.name || '', contentX + 12, statusRowY + 7);

        if (hasDeps) {
          ctx.fillStyle = TEXT_SECONDARY;
          ctx.font = '10px Inter, -apple-system, system-ui, sans-serif';
          ctx.fillText('\u{1F517}', barX + barW - 16, statusRowY + 7);
        }
      };

      // ── Render groups for this quarter chunk ──
      for (const group of groups) {
        const hasVisible = group.subProducts.some(sp =>
          sp.initiatives.some(init => {
            const si = quarters.findIndex(q => q.key === init.startQuarter);
            const rawEi = init.endQuarter ? quarters.findIndex(q => q.key === init.endQuarter) : si;
            const ei = rawEi >= 0 ? rawEi : si;
            return si <= chunkEnd && ei >= chunkStart;
          })
        );
        if (!hasVisible) continue;

        if (curY + TL_PRODUCT_ROW_H > PDF_CONTENT_END_Y) newPage();
        drawProdRow(pg.ctx, group, curY);
        curY += TL_PRODUCT_ROW_H;

        for (const sp of group.subProducts) {
          const visInits = sp.initiatives.filter(init => {
            const si = quarters.findIndex(q => q.key === init.startQuarter);
            const rawEi = init.endQuarter ? quarters.findIndex(q => q.key === init.endQuarter) : si;
            const ei = rawEi >= 0 ? rawEi : si;
            return si <= chunkEnd && ei >= chunkStart;
          });
          if (visInits.length === 0) continue;

          const minH = TL_SUBPROD_ROW_H + TL_ROW_PAD + TL_BAR_H + TL_ROW_PAD;
          if (curY + minH > PDF_CONTENT_END_Y) {
            newPage();
            drawProdRow(pg.ctx, group, curY);
            curY += TL_PRODUCT_ROW_H;
          }

          drawSpRow(pg.ctx, sp.name, curY);
          curY += TL_SUBPROD_ROW_H;
          curY += TL_ROW_PAD;

          for (const init of visInits) {
            if (curY + TL_BAR_H + TL_ROW_PAD > PDF_CONTENT_END_Y) {
              newPage();
              drawProdRow(pg.ctx, group, curY);
              curY += TL_PRODUCT_ROW_H;
              drawSpRow(pg.ctx, `${sp.name} (cont.)`, curY);
              curY += TL_SUBPROD_ROW_H;
              curY += TL_ROW_PAD;
            }
            drawBar(pg.ctx, init, curY);
            curY += TL_BAR_H + TL_BAR_GAP;
          }
          curY += TL_ROW_PAD;
        }
      }

      drawLegend(pg.ctx, classifications, statuses, PDF_LEGEND_Y, pageW);
    }
  } else {
    // ── List view: vertical pagination only ──
    const pageW = LI_TABLE_W;
    const cols = [
      { label: 'Initiative Name', x: 0, w: LI_COL_NAME },
      { label: 'Classification', x: LI_COL_NAME, w: LI_COL_CLASS },
      { label: 'Status', x: LI_COL_NAME + LI_COL_CLASS, w: LI_COL_STATUS },
      { label: 'Start', x: LI_COL_NAME + LI_COL_CLASS + LI_COL_STATUS, w: LI_COL_START },
      { label: 'End', x: LI_COL_NAME + LI_COL_CLASS + LI_COL_STATUS + LI_COL_START, w: LI_COL_END },
    ];

    let pg = createPdfPage(pageW);
    pages.push(pg);
    drawHeader(pg.ctx, options, pageW);
    let curY = HEADER_H;

    const drawTableHeaders = (ctx: CanvasRenderingContext2D, y: number) => {
      ctx.fillStyle = HEADER_BG;
      ctx.fillRect(0, y, pageW, LI_HEADER_H);
      ctx.fillStyle = TEXT_PRIMARY;
      ctx.font = 'bold 10px Inter, -apple-system, system-ui, sans-serif';
      cols.forEach(col => {
        ctx.fillText(col.label, col.x + LI_PAD_LEFT, y + LI_HEADER_H / 2 + 3.5);
      });
      ctx.strokeStyle = BORDER_DEFAULT;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, y + LI_HEADER_H);
      ctx.lineTo(pageW, y + LI_HEADER_H);
      ctx.stroke();
    };

    drawTableHeaders(pg.ctx, curY);
    curY += LI_HEADER_H;

    const newPage = () => {
      drawLegend(pg.ctx, classifications, statuses, PDF_LEGEND_Y, pageW);
      pg = createPdfPage(pageW);
      pages.push(pg);
      drawHeader(pg.ctx, options, pageW);
      curY = HEADER_H;
      drawTableHeaders(pg.ctx, curY);
      curY += LI_HEADER_H;
    };

    const drawProdRow = (ctx: CanvasRenderingContext2D, group: ProductGroup, y: number) => {
      const count = group.subProducts.reduce((s, sp) => s + sp.initiatives.length, 0);
      ctx.fillStyle = PRODUCT_BG;
      ctx.fillRect(0, y, pageW, LI_ROW_H);
      ctx.fillStyle = TEXT_PRIMARY;
      ctx.font = 'bold 10px Inter, -apple-system, system-ui, sans-serif';
      ctx.fillText(`${group.product.name} (${count})`, LI_PAD_LEFT, y + LI_ROW_H / 2 + 3.5);
      ctx.strokeStyle = BORDER_LIGHT;
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(0, y + LI_ROW_H);
      ctx.lineTo(pageW, y + LI_ROW_H);
      ctx.stroke();
    };

    const drawSpRow = (ctx: CanvasRenderingContext2D, name: string, y: number) => {
      ctx.fillStyle = SUBPROD_BG;
      ctx.fillRect(0, y, pageW, LI_ROW_H);
      ctx.fillStyle = TEXT_SECONDARY;
      ctx.font = '500 10px Inter, -apple-system, system-ui, sans-serif';
      ctx.fillText(name, 16, y + LI_ROW_H / 2 + 3.5);
      ctx.strokeStyle = BORDER_LIGHT;
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(0, y + LI_ROW_H);
      ctx.lineTo(pageW, y + LI_ROW_H);
      ctx.stroke();
    };

    const drawInitRow = (ctx: CanvasRenderingContext2D, init: Initiative, y: number) => {
      const clsColor = init.classification?.color || '#3B82F6';
      const statusColor = init.status?.color || '#6B7280';
      const hasDeps = init.dependencies && init.dependencies.length > 0;
      const startQ = quarters.find(q => q.key === init.startQuarter);
      const endQ = init.endQuarter ? quarters.find(q => q.key === init.endQuarter) : null;
      const midY = y + LI_INIT_ROW_H / 2;

      ctx.fillStyle = BG_WHITE;
      ctx.fillRect(0, y, pageW, LI_INIT_ROW_H);

      // Classification color pipe
      ctx.fillStyle = clsColor;
      ctx.fillRect(cols[0].x + 22, y + 4, 3, LI_INIT_ROW_H - 8);

      // Name (wrapped up to 3 lines) + dependency icon
      ctx.fillStyle = TEXT_PRIMARY;
      ctx.font = '500 10px Inter, -apple-system, system-ui, sans-serif';
      const depSuffix = hasDeps ? '  \u{1F517}' : '';
      const titleLines = wrapText(ctx, init.title + depSuffix, LI_COL_NAME - 36, LI_TITLE_MAX_LINES);
      titleLines.forEach((line, li) => {
        ctx.fillText(line, cols[0].x + 30, y + 14 + li * LI_TITLE_LINE_H);
      });

      // Classification
      ctx.fillStyle = clsColor;
      ctx.fillRect(cols[1].x + LI_PAD_LEFT, midY - 4, 7, 7);
      ctx.fillStyle = TEXT_PRIMARY;
      ctx.font = '9px Inter, -apple-system, system-ui, sans-serif';
      ctx.fillText(truncateText(ctx, init.classification?.name || '', LI_COL_CLASS - 26), cols[1].x + LI_PAD_LEFT + 11, midY + 3);

      // Status
      ctx.fillStyle = statusColor;
      ctx.beginPath();
      ctx.arc(cols[2].x + LI_PAD_LEFT + 4, midY, 3.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = TEXT_PRIMARY;
      ctx.font = '9px Inter, -apple-system, system-ui, sans-serif';
      ctx.fillText(init.status?.name || '', cols[2].x + LI_PAD_LEFT + 13, midY + 3);

      // Start / End
      ctx.fillStyle = TEXT_PRIMARY;
      ctx.font = '9px Inter, -apple-system, system-ui, sans-serif';
      ctx.fillText(startQ?.fullLabel || '', cols[3].x + LI_PAD_LEFT, midY + 3);
      ctx.fillText(endQ?.fullLabel || '\u2013', cols[4].x + LI_PAD_LEFT, midY + 3);

      // Bottom border
      ctx.strokeStyle = BORDER_LIGHT;
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(0, y + LI_INIT_ROW_H);
      ctx.lineTo(pageW, y + LI_INIT_ROW_H);
      ctx.stroke();
    };

    for (const group of groups) {
      if (curY + LI_ROW_H > PDF_CONTENT_END_Y) newPage();
      drawProdRow(pg.ctx, group, curY);
      curY += LI_ROW_H;

      for (const sp of group.subProducts) {
        if (curY + LI_ROW_H * 2 > PDF_CONTENT_END_Y) {
          newPage();
          drawProdRow(pg.ctx, group, curY);
          curY += LI_ROW_H;
        }
        drawSpRow(pg.ctx, sp.name, curY);
        curY += LI_ROW_H;

        for (const init of sp.initiatives) {
          if (curY + LI_INIT_ROW_H > PDF_CONTENT_END_Y) {
            newPage();
            drawProdRow(pg.ctx, group, curY);
            curY += LI_ROW_H;
            drawSpRow(pg.ctx, `${sp.name} (cont.)`, curY);
            curY += LI_ROW_H;
          }
          drawInitRow(pg.ctx, init, curY);
          curY += LI_INIT_ROW_H;
        }
      }
    }

    drawLegend(pg.ctx, classifications, statuses, PDF_LEGEND_Y, pageW);
  }

  // Add page numbers (now we know the total)
  const total = pages.length;
  for (let i = 0; i < total; i++) {
    const p = pages[i];
    p.ctx.fillStyle = TEXT_TERTIARY;
    p.ctx.font = '10px Inter, -apple-system, system-ui, sans-serif';
    const text = `Page ${i + 1} of ${total}`;
    const tw = p.ctx.measureText(text).width;
    p.ctx.fillText(text, p.width - tw - 16, 56);
  }

  // Assemble PDF document
  const timestamp = getTimestamp();
  const firstW = pages[0].width;
  const pdf = new jsPDF({
    orientation: firstW > PDF_PAGE_H ? 'landscape' : 'portrait',
    unit: 'px',
    format: [firstW, PDF_PAGE_H],
  });

  for (let i = 0; i < pages.length; i++) {
    if (i > 0) {
      const w = pages[i].width;
      pdf.addPage([w, PDF_PAGE_H], w > PDF_PAGE_H ? 'landscape' : 'portrait');
    }
    const imgData = pages[i].canvas.toDataURL('image/png');
    pdf.addImage(imgData, 'PNG', 0, 0, pages[i].width, PDF_PAGE_H);
  }

  pdf.save(`${options.viewType}_${timestamp}.pdf`);
}

// ── Main export function ────────────────────────────────────────────────────────

export async function exportCanvasView(
  options: CanvasExportOptions,
): Promise<{ success: boolean; error?: string }> {
  try {
    const { viewType, format, quarters, classifications, statuses } = options;
    const groups = groupByProduct(options.initiatives, options.products);

    // Sort initiatives by start quarter within each sub-product
    for (const group of groups) {
      for (const sp of group.subProducts) {
        sp.initiatives.sort((a, b) => {
          const aIdx = quarters.findIndex(q => q.key === a.startQuarter);
          const bIdx = quarters.findIndex(q => q.key === b.startQuarter);
          return aIdx - bIdx;
        });
      }
    }

    // PDF: paginated multi-page export
    if (format === 'pdf') {
      exportPaginatedPdf(options, groups);
      return { success: true };
    }

    // PNG: single canvas export
    const dims = viewType === 'timeline'
      ? calcTimelineDimensions(groups, quarters.length)
      : calcListDimensions(groups);

    const canvas = document.createElement('canvas');
    canvas.width = dims.width * SCALE;
    canvas.height = dims.height * SCALE;
    const ctx = canvas.getContext('2d')!;
    ctx.scale(SCALE, SCALE);

    ctx.fillStyle = BG_WHITE;
    ctx.fillRect(0, 0, dims.width, dims.height);

    drawHeader(ctx, options, dims.width);

    if (viewType === 'timeline') {
      drawTimeline(ctx, options, groups);
    } else {
      drawList(ctx, options, groups);
    }

    const legendY = dims.height - LEGEND_H;
    drawLegend(ctx, classifications, statuses, legendY, dims.width);

    const timestamp = getTimestamp();
    const filename = `${viewType}_${timestamp}`;
    const link = document.createElement('a');
    link.download = `${filename}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();

    return { success: true };
  } catch (error) {
    console.error('Canvas export failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Export failed',
    };
  }
}
